<template>
  <!-- prettier-ignore -->
  <div class="aide">
    <template v-if="browser.isDesktop">
      <Form.Dropdown
        class="aide__language"
        name="language"
        hide-chevron
        :model-value="$props.language"
        :options="languages"
        @update:modelValue="changeLanguage"
      />

      <a
        class="aide__exit"
        @click="triggerExit"
      >
        <span
          class="aide__text"
          v-text="$t('aide.screen.exit.button')"
        />
        <the-icon
          class="aide__icon"
          art="light"
          name="right-from-bracket"
        />
      </a>
    </template>
    <template v-else>
      <a
        class="aide__menu"
        @click="triggerOpen"
      >
        <span
          class="aide__text"
          v-text="$t('aide.screen.aide.menu')"
        />
        <the-icon
          class="aide__icon"
          art="light"
          name="bars"
        />
      </a>
    </template>
  </div>
</template>

<script setup>
import { events$, loading$ } from '@/services'

import useBasket from '@/hooks/useBasket'
import useBrowser from '@/hooks/useBrowser'

import * as Form from '@/components/Form'

import { EVENT_AIDE } from '@/config/events'
import { SUPPORTED_LANGUAGES } from '@/config/constants'

// HOOKS
const { updateLanguage } = useBasket()
const { browser } = useBrowser()

// INIT
defineEmits(['language'])
defineProps({
  language: {
    type: String,
    required: true,
  },
})

// DATA
const languages = SUPPORTED_LANGUAGES

// METHODS
async function changeLanguage(lang) {
  loading$.start()
  await updateLanguage({ lang, isAide: true })
  loading$.end()
}

function triggerExit() {
  events$.emit(EVENT_AIDE.EXIT)
}

function triggerOpen() {
  events$.emit(EVENT_AIDE.MENU_OPEN)
}
</script>

<style name="animations" scoped>
.slide-down-enter-active,
.slide-down-leave-active,
.slide-up-enter-active,
.slide-up-leave-active {
  transition-duration: 0.5s;
  transition-property: height, opacity, transform;
  transition-timing-function: cubic-bezier(0.55, 0, 0.1, 1);
  overflow: hidden;
}

.slide-down-enter,
.slide-up-leave-active,
.slide-down-leave-active,
.slide-up-enter {
  transform: translate(0, 100%);
}
</style>

<style name="mobile" scoped>
.aide {
  display: flex;
  gap: 10px;
  justify-content: flex-end;

  .icon {
    color: var(--c-primary-neutral-1);
  }
}

.aide__language {
  height: 35px;
  line-height: 35px;

  & :deep(select) {
    font-size: 12px;
    font-weight: 600;
    color: var(--c-primary-neutral-1);
  }

  & :deep(.dropdown) {
    height: auto;
  }

  & :deep(.dropdown__item) {
    border-bottom: none;
    padding-top: 0;
  }
}

.aide__exit,
.aide__person,
.aide__menu {
  display: inline-block;
  color: var(--c-primary-neutral-1);
  height: 35px;
  line-height: 35px;
}

.aide__text {
  display: inline-block;
  height: 20px;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  padding: 0 5px 0 0;
}

.aide__icon {
  color: inherit;
  vertical-align: middle;
}

.aide__personMenu {
  display: flex;
  padding: 7px 0 0 0;

  .icon {
    margin: 3px 0 0 5px;
  }

  .aide__text {
    max-width: 75px;
  }
}

.aide__overlay {
  position: fixed;
  top: 65px;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 11;

  display: flex;
  flex-direction: column;

  background-color: rgba(0, 0, 0, 0.5);

  .aide__person {
    display: inline-block;
    height: 40px;
    line-height: 40px;
    padding: 0 24px;
    font-weight: normal;
    background-color: var(--c-primary-neutral-3);

    &:first-child {
      border-top: 1px solid var(--c-secondary-neutral-3);
    }

    &:last-child {
      box-shadow: 0px 3px 3px -1px rgba(0, 0, 0, 0.14);
    }

    &:hover {
      background-color: var(--c-secondary-neutral-3);
    }

    &--active {
      font-weight: bold;
    }
  }
}
</style>
